import type {
  Exercise,
  ExerciseListItemDto,
  Gym,
  Member,
  Trainer,
  Muscle,
  Station,
  TrainingRoutineTemplate,
  TrainingRoutineSession,
  GymProduct,
  Manager,
  Program,
  SessionStatistic,
  Group,
  JetsonHealthDTO,
  PagedResponse,
} from '../types';
import {
  isExercise,
  isExerciseListItem,
  isGym,
  isMember,
  isTrainer,
  isMuscle,
  isStation,
  isTemplate,
  isTrainingRoutineSession,
  isGymProduct,
  isManager,
  isProgram,
  isSessionStatistic,
  isGroup,
  isJetsonHealth,
  isRecordOfTrainingRoutineSessions,
  isRecord,
} from '../utils/typeUtils';

type Guard<T> = (value: unknown) => value is T;

const createArrayValidator =
  <T>(guard: Guard<T>) =>
  (data: unknown): data is T[] => {
    if (!Array.isArray(data)) return false;
    return data.every((item) => guard(item));
  };

const createPageValidator =
  <T>(guard: Guard<T>) =>
  (data: unknown): data is PagedResponse<T> => {
    if (!isRecord(data)) return false;
    const content = (data as { content?: unknown[] }).content;
    if (!Array.isArray(content)) return false;
    return content.every((item) => guard(item));
  };

const createFilteringPageValidator =
  <T>(guard: Guard<T>) =>
  (data: unknown): data is PagedResponse<T> => {
    if (!isRecord(data)) return false;
    const content = (data as { content?: unknown[] }).content;
    if (!Array.isArray(content)) return false;
    // Filter out invalid items instead of rejecting the whole response
    (data as { content: unknown[] }).content = content.filter((item) => guard(item));
    return true;
  };

export const isValidMembersResponse = createPageValidator<Member>(isMember);

export const isValidTrainersResponse = createPageValidator<Trainer>(isTrainer);

export const isValidGymProductResponse =
  createArrayValidator<GymProduct>(isGymProduct);

export const isValidGymProductsPayload = (
  data: unknown,
): data is GymProduct[] | PagedResponse<GymProduct> => {
  return (
    createArrayValidator<GymProduct>(isGymProduct)(data) ||
    createPageValidator<GymProduct>(isGymProduct)(data)
  );
};

export const isValidMuscleGroupResponse = createFilteringPageValidator<Muscle>(isMuscle);

export const isValidMemberResponse = (data: unknown): data is Member | null => {
  return data === null || isMember(data);
};

export const isValidTrainerResponse = (data: unknown): data is Trainer => {
  return isTrainer(data);
};

export const isValidManagerResponse = (data: unknown): data is Manager => {
  return isManager(data);
};

export const isValidManagersResponse = createPageValidator<Manager>(isManager);

export const isValidStationsResponse = createPageValidator<Station>(isStation);

export const isValidTemplateResponse = (
  data: unknown,
): data is TrainingRoutineTemplate => {
  return isTemplate(data);
};

export const isValidTemplatesResponse =
  createPageValidator<TrainingRoutineTemplate>(isTemplate);

export const isValidProgramResponse = (data: unknown): data is Program => {
  return isProgram(data);
};

export const isValidProgramsResponse = createPageValidator<Program>(isProgram);

export const isValidExerciseResponse = (data: unknown): data is Exercise => {
  return isExercise(data);
};

export const isValidExercisesResponse =
  createPageValidator<Exercise>(isExercise);
export const isValidSourceExercisesResponse =
  createFilteringPageValidator<Exercise>(isExercise);

export const isValidExerciseListResponse =
  createPageValidator<ExerciseListItemDto>(isExerciseListItem);

export const isValidGymResponse = (data: unknown): data is Gym => {
  return isGym(data);
};

export const isValidGymsResponse = createPageValidator<Gym>(isGym);
export const isValidGroupResponse = (data: unknown): data is Group => {
  return isGroup(data);
};

export const isValidGroupsResponse = createPageValidator<Group>(isGroup);

export const isValidTrainingRoutineSessionsResponse =
  createPageValidator<TrainingRoutineSession>(isTrainingRoutineSession);

export const isValidSessionStatisticsResponse =
  createPageValidator<SessionStatistic>(isSessionStatistic);

export const isValidJetsonsHealthResponse = (
  data: unknown,
): data is JetsonHealthDTO[] | PagedResponse<JetsonHealthDTO> => {
  return (
    createArrayValidator<JetsonHealthDTO>(isJetsonHealth)(data) ||
    createPageValidator<JetsonHealthDTO>(isJetsonHealth)(data)
  );
};

export const isValidLastSessionsResponse = (
  data: unknown,
): data is Record<string, TrainingRoutineSession> => {
  return isRecordOfTrainingRoutineSessions(data);
};
